import React, { useState, useMemo, useEffect } from 'react';
import { formatTableDate } from '../utils/dateFormatter';
import { CONFIG } from '../config/environment';

// Import the LinkPreview component from SheetsTable
const extractVideoId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Efficient LinkPreview component using batched videoMetadata prop
const LinkPreview: React.FC<{ url: string; videoMetadata?: Record<string, any> }> = ({ url, videoMetadata }) => {
  const videoId = extractVideoId(url);
  const [localMetadata, setLocalMetadata] = useState<any>(null);

  // Fallback: fetch metadata locally if not provided via props
  useEffect(() => {
    if (videoId && !videoMetadata?.[videoId] && !localMetadata) {
      // Only fetch if we don't have metadata from props and haven't fetched locally yet
      fetch(`${CONFIG.API_BASE_URL}/api/youtube/videos/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoIds: [videoId] })
      })
      .then(res => res.json())
      .then(data => {
        if (data[videoId]) {
          setLocalMetadata(data[videoId]);
        }
      })
      .catch(err => console.log('Failed to fetch local metadata:', err));
    }
  }, [videoId, videoMetadata, localMetadata]);

  // Parse URL to extract domain and path info
  const parseUrl = (urlString: string) => {
    try {
      const urlObj = new URL(urlString);
      const domain = urlObj.hostname.replace(/^www\./, '');
      const path = urlObj.pathname;
      const pathSegments = path.split('/').filter(segment => segment.length > 0);

      return {
        domain,
        path,
        pathSegments,
        protocol: urlObj.protocol
      };
    } catch (error) {
      return {
        domain: 'Unknown',
        path: '',
        pathSegments: [],
        protocol: 'https:'
      };
    }
  };

  const urlInfo = parseUrl(url);


  // Get appropriate icon based on domain
  const getDomainIcon = (domain: string) => {
    if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    }
    if (domain.includes('twitter.com') || domain.includes('x.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    }
    if (domain.includes('instagram.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    }
    if (domain.includes('facebook.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    }
    // Default link icon
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 6V8H5V19H16V14H18V20A1 1 0 0 1 17 21H4A1 1 0 0 1 3 20V7A1 1 0 0 1 4 6H10ZM21 3V11H19L19 6.413L11.207 14.207L9.793 12.793L17.585 5H13V3H21Z"/>
      </svg>
    );
  };

  // For YouTube URLs, use rich preview with batched metadata
  if (videoId) {
    // Use prop metadata first, then local metadata, then fallback
    const metadata = videoMetadata?.[videoId] || localMetadata;
    const thumbnailUrl = metadata?.thumbnail || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    const title = metadata?.title || `Video ${videoId}`;
    const channelTitle = metadata?.channelTitle || 'YouTube';
    const viewCount = metadata?.viewCount ?
      new Intl.NumberFormat().format(parseInt(metadata.viewCount)) : null;
    const likeCount = metadata?.likeCount ?
      new Intl.NumberFormat().format(parseInt(metadata.likeCount)) : null;

    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        textDecoration: 'none',
        padding: '0.5rem',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        background: 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
        transition: 'all 0.2s',
        maxWidth: '320px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)';
        e.currentTarget.style.borderColor = '#1DB584';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(29, 181, 132, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)';
        e.currentTarget.style.borderColor = '#E5E7EB';
        e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
      }}>
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          style={{
            width: '60px',
            height: '45px',
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0
          }}
          onError={(e) => {
            e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
          }}
        />
        <div style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#1F2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '240px'
          }}
          title={title}>
            {title}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#4B5563',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {getDomainIcon(urlInfo.domain)}
              {channelTitle}
            </span>
            {viewCount && (
              <span>• 👁️ {viewCount}</span>
            )}
            {likeCount && (
              <span>• 👍 {likeCount}</span>
            )}
          </div>
        </div>
      </a>
    );
  }

  // For non-YouTube URLs, use styled link preview
  const displayTitle = urlInfo.pathSegments.length > 0
    ? urlInfo.pathSegments[urlInfo.pathSegments.length - 1].replace(/[-_]/g, ' ')
    : urlInfo.domain;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      textDecoration: 'none',
      padding: '0.5rem',
      borderRadius: '8px',
      border: '1px solid #E5E7EB',
      background: 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
      transition: 'all 0.2s',
      maxWidth: '320px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)';
      e.currentTarget.style.borderColor = '#1DB584';
      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(29, 181, 132, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)';
      e.currentTarget.style.borderColor = '#E5E7EB';
      e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
    }}>
      <div style={{
        width: '60px',
        height: '45px',
        borderRadius: '6px',
        backgroundColor: '#F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#4B5563'
      }}>
        {getDomainIcon(urlInfo.domain)}
      </div>
      <div style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem'
      }}>
        <div style={{
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#1F2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '240px',
          textTransform: 'capitalize'
        }}
        title={displayTitle}>
          {displayTitle}
        </div>
        <div style={{
          fontSize: '0.75rem',
          color: '#4B5563',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {urlInfo.domain}
          </span>
        </div>
      </div>
    </a>
  );
};

interface PaymentData {
  id?: number;
  orgId: string;
  smLink: string;
  firstName: string;
  lastName: string;
  amount: string;
  paymentDate: string;
  createdAt?: string;
}

interface PaymentsTableProps {
  data: any[][];
  userId: string;
  structuredData?: PaymentData[];
  onDeletePayment?: (paymentId: number) => Promise<void>;
  videoMetadata?: Record<string, any>;
}

const PaymentsTable: React.FC<PaymentsTableProps> = ({ data, userId, structuredData, onDeletePayment, videoMetadata }) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [selectedPayments, setSelectedPayments] = useState<Set<number>>(new Set());
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [deletingPaymentIds, setDeletingPaymentIds] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const headers = data?.[0] || [];
  const orgIdIndex = headers.findIndex((h: string) => h === 'OrgID' || h === 'Org ID');

  // Filter and sort rows
  const processedRows = useMemo(() => {
    if (!data || data.length === 0) return [];

    const filteredRows = data.slice(1).filter(row => {
      return row[orgIdIndex] === userId;
    });

    // Apply sorting
    if (sortColumn && sortDirection) {
      const columnIndex = headers.findIndex((h: string) => h === sortColumn);
      if (columnIndex !== -1) {
        filteredRows.sort((a, b) => {
          let aVal: any = a[columnIndex] || '';
          let bVal: any = b[columnIndex] || '';

          // Special handling for amounts
          if (sortColumn === 'Amount') {
            aVal = parseFloat(aVal.toString().replace(/[$,]/g, '')) || 0;
            bVal = parseFloat(bVal.toString().replace(/[$,]/g, '')) || 0;
          } else {
            aVal = aVal.toString().toLowerCase();
            bVal = bVal.toString().toLowerCase();
          }

          if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }

    return filteredRows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, userId, sortColumn, sortDirection]);

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortColumn(null);
      }
    } else {
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (columnName: string) => {
    if (sortColumn !== columnName) return '↕️';
    if (sortDirection === 'asc') return '⬆️';
    if (sortDirection === 'desc') return '⬇️';
    return '↕️';
  };

  const handleSelectPayment = (paymentId: number) => {
    const newSelected = new Set(selectedPayments);
    if (newSelected.has(paymentId)) {
      newSelected.delete(paymentId);
    } else {
      newSelected.add(paymentId);
    }
    setSelectedPayments(newSelected);
  };

  const handleSelectAll = () => {
    if (!structuredData) return;

    const allPaymentIds = structuredData
      .filter(p => p.orgId === userId && p.id !== undefined)
      .map(p => p.id!)
      .filter(id => processedRows.some(row => {
        const payment = structuredData.find(p =>
          p.orgId === userId &&
          p.smLink === row[headers.findIndex(h => h === 'SM Link')] &&
          p.firstName === row[headers.findIndex(h => h === 'First Name')] &&
          p.lastName === row[headers.findIndex(h => h === 'Last Name')]
        );
        return payment?.id === id;
      }));

    if (selectedPayments.size === allPaymentIds.length) {
      setSelectedPayments(new Set());
    } else {
      setSelectedPayments(new Set(allPaymentIds));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedPayments.size === 0) return;
    setShowDeleteConfirm(true);
    setShowActionsDropdown(false);
  };

  const handleDeleteConfirm = async () => {
    if (!onDeletePayment || selectedPayments.size === 0) return;

    setDeletingPaymentIds(new Set(selectedPayments));
    setShowDeleteConfirm(false);

    try {
      // Delete payments one by one
      const paymentIds = Array.from(selectedPayments);
      for (const paymentId of paymentIds) {
        await onDeletePayment(paymentId);
      }
      setSelectedPayments(new Set());
    } catch (error) {
      console.error('Error deleting payments:', error);
    } finally {
      setDeletingPaymentIds(new Set());
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <p>No payment data available</p>
      </div>
    );
  }

  if (processedRows.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <p>No payments found for organization: <strong>{userId}</strong></p>
        <p style={{ fontSize: '14px', marginTop: '8px' }}>
          Make sure your Payments sheet contains data with OrgID matching your login email prefix.
        </p>
      </div>
    );
  }

  // Check if we have structured data with IDs (database format)
  const hasIds = structuredData && structuredData.length > 0 && structuredData.some((payment: any) => payment.id !== undefined);

  // Filter out OrgID and Trackable Link columns from display
  const visibleHeaders = headers.filter(header =>
    header !== 'OrgID' &&
    header !== 'Org ID' &&
    header !== 'Trackable Link'
  );

  // Get current page payment IDs for selection
  const currentPagePaymentIds = processedRows
    .map(row => {
      const payment = structuredData?.find(p =>
        p.orgId === userId &&
        p.smLink === row[headers.findIndex(h => h === 'SM Link')] &&
        p.firstName === row[headers.findIndex(h => h === 'First Name')] &&
        p.lastName === row[headers.findIndex(h => h === 'Last Name')]
      );
      return payment?.id;
    })
    .filter(id => id !== undefined) as number[];

  const allCurrentPageSelected = currentPagePaymentIds.length > 0 &&
    currentPagePaymentIds.every(id => selectedPayments.has(id));
  const someCurrentPageSelected = currentPagePaymentIds.some(id => selectedPayments.has(id));

  return (
    <div>
      {/* Table Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '12px',
        color: '#6b7280',
        marginBottom: '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            Showing {processedRows.length} payment{processedRows.length !== 1 ? 's' : ''}
            {selectedPayments.size > 0 && (
              <span style={{ marginLeft: '8px', fontWeight: '500', color: '#059669' }}>
                ({selectedPayments.size} selected)
              </span>
            )}
          </div>

          {hasIds && onDeletePayment && selectedPayments.size > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                style={{
                  background: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#4F46E5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#6366F1';
                }}
              >
                Actions ({selectedPayments.size})
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>

              {showActionsDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  zIndex: 1000,
                  minWidth: '120px',
                  marginTop: '2px'
                }}>
                  <button
                    onClick={handleDeleteSelected}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      fontSize: '12px',
                      color: '#EF4444',
                      cursor: 'pointer',
                      borderRadius: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#FEF2F2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    🗑️ Delete Selected
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ fontSize: '11px', opacity: 0.7 }}>
          Click headers to sort
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              {hasIds && onDeletePayment && (
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  width: '50px'
                }}>
                  <input
                    type="checkbox"
                    checked={allCurrentPageSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someCurrentPageSelected && !allCurrentPageSelected;
                      }
                    }}
                    onChange={handleSelectAll}
                    style={{
                      cursor: 'pointer',
                      transform: 'scale(1.1)'
                    }}
                  />
                </th>
              )}
              {visibleHeaders.map((header, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(header)}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {header}
                    <span style={{ fontSize: '10px', opacity: 0.7 }}>
                      {getSortIcon(header)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedRows.map((row, rowIndex) => {
              // Find the payment ID from structured data
              const payment = structuredData?.find(p =>
                p.orgId === userId &&
                p.smLink === row[headers.findIndex(h => h === 'SM Link')] &&
                p.firstName === row[headers.findIndex(h => h === 'First Name')] &&
                p.lastName === row[headers.findIndex(h => h === 'Last Name')]
              );
              const paymentId = payment?.id;
              const isSelected = paymentId ? selectedPayments.has(paymentId) : false;
              const isDeleting = paymentId ? deletingPaymentIds.has(paymentId) : false;

              return (
                <tr
                  key={rowIndex}
                  style={{
                    backgroundColor: isSelected ? '#F3F4F6' : 'white',
                    borderBottom: '1px solid #e5e7eb',
                    transition: 'background-color 0.15s ease',
                    opacity: isDeleting ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? '#F3F4F6' : 'white';
                  }}
                >
                  {hasIds && onDeletePayment && (
                    <td style={{
                      padding: '12px 16px',
                      width: '50px'
                    }}>
                      {paymentId && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectPayment(paymentId)}
                          disabled={isDeleting}
                          style={{
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            transform: 'scale(1.1)'
                          }}
                        />
                      )}
                    </td>
                  )}
                  {visibleHeaders.map((header) => {

                  const cellIndex = headers.findIndex(h => h === header);
                  const cell = row[cellIndex];
                  let cellContent: any = cell;
                  let cellStyle: React.CSSProperties = {
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#374151'
                  };

                  // Style specific columns
                  if (header === 'Amount') {
                    cellStyle.fontWeight = '600';
                    // Handle empty, null, or undefined values
                    if (!cell || cell === '' || cell === null || cell === undefined) {
                      cellContent = '$0.00';
                    }
                    // Format as currency if it's a number
                    else if (!isNaN(parseFloat(cell.toString().replace(/[$,]/g, '')))) {
                      const amount = parseFloat(cell.toString().replace(/[$,]/g, ''));
                      cellContent = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      }).format(amount);
                      if (amount > 0) {
                        cellStyle.color = '#16a34a';
                      }
                    }
                  } else if (header === 'SM Link') {
                    // Use link preview component for SM Link
                    cellStyle.whiteSpace = 'normal';
                    cellStyle.padding = '8px';

                    cellContent = <LinkPreview url={cell} videoMetadata={videoMetadata} />;
                  } else if (header === 'Payment Date') {
                    // Format payment dates for better readability
                    cellContent = formatTableDate(cell);
                    cellStyle.fontWeight = '500';
                    cellStyle.color = '#4B5563';
                  } else if (header === 'First Name' || header === 'Last Name') {
                    cellStyle.fontWeight = '500';
                  }

                  return (
                    <td
                      key={header}
                      style={cellStyle}
                    >
                      {cellContent}
                    </td>
                  );
                })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '12px'
            }}>
              Delete Payment{selectedPayments.size > 1 ? 's' : ''}
            </div>

            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '20px'
            }}>
              Are you sure you want to delete {selectedPayments.size} payment{selectedPayments.size > 1 ? 's' : ''}? This action cannot be undone.
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleDeleteConfirm}
                style={{
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#DC2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#EF4444';
                }}
              >
                Delete {selectedPayments.size > 1 ? `${selectedPayments.size} Payments` : 'Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showActionsDropdown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setShowActionsDropdown(false)}
        />
      )}
    </div>
  );
};

export default PaymentsTable;